/**
 * Creazione di azienda, persona di riferimento e collegamento tra le due,
 * a partire dai dati raccolti nel pannello email.
 *
 * Le dipendenze sono iniettate invece di essere importate: cosi` questo modulo
 * resta testabile in ambiente node, senza Supabase.
 */
import type { ExistingMatches } from './companyProspect';

export type CreateKind = 'person' | 'company' | 'both';

export interface PersonValues {
  business_name: string;
  email: string;
  phone: string;
  contact_role: string;
}

export interface CreatedEntity {
  id: string;
  business_name: string;
}

export interface CreateFromProspectDeps {
  createClient: (
    data: Record<string, unknown>,
    options?: { asReferente?: boolean },
  ) => Promise<CreatedEntity | null>;
  linkPersonToCompany: (input: {
    person_client_id: string;
    company_client_id: string;
    role?: string | null;
    is_referente?: boolean;
    is_primary_contact?: boolean;
    is_primary_company?: boolean;
  }) => Promise<unknown>;
  /** Evita di duplicare un collegamento gia` presente. Se assente, si assume che manchi. */
  linkExists?: (personId: string, companyId: string) => Promise<boolean>;
}

export interface CreateFromProspectInput {
  kind: CreateKind;
  company: Record<string, string>;
  person: PersonValues;
  /** Altri partecipanti del thread da creare come referenti aggiuntivi. */
  extraPeople: { email: string; name: string | null }[];
  pipelineId: string | null;
  /** 'gmail' oppure 'outlook', secondo il provider del thread. */
  leadSource: string;
  existing: ExistingMatches | null;
  /** Dominio o sito usato per l'analisi, salvato come dettaglio della fonte. */
  websiteHint: string | null;
}

export interface CreateFromProspectResult {
  companyId: string | null;
  personId: string | null;
  companyCreated: boolean;
  personCreated: boolean;
  extraCreated: number;
  label: string;
}

const COMPANY_FIELDS = [
  'vat_number', 'website', 'phone', 'email', 'pec', 'city',
  'postal_code', 'province', 'street_address', 'street_number', 'logo_url',
] as const;

export async function createFromProspect(
  input: CreateFromProspectInput,
  deps: CreateFromProspectDeps,
): Promise<CreateFromProspectResult> {
  const wantsCompany = input.kind === 'company' || input.kind === 'both';
  const wantsPerson = input.kind === 'person' || input.kind === 'both';

  let companyId = wantsCompany ? (input.existing?.company?.id ?? null) : null;
  let personId = wantsPerson ? (input.existing?.person?.id ?? null) : null;
  let companyCreated = false;
  let personCreated = false;
  let extraCreated = 0;
  let companyName = input.existing?.company?.business_name ?? '';
  let personName = input.existing?.person?.business_name ?? '';

  // 1. Azienda: e` lei il lead, quindi riceve stato e pipeline.
  if (wantsCompany && !companyId) {
    const data: Record<string, unknown> = {
      entity_type: 'company',
      business_name: input.company.business_name?.trim() || '',
      lead_source: input.leadSource,
      lead_source_details: input.websiteHint || undefined,
      pipeline_id: input.pipelineId || null,
    };
    for (const f of COMPANY_FIELDS) {
      const v = input.company[f]?.trim();
      if (v) data[f] = v;
    }
    const created = await deps.createClient(data);
    if (!created) throw new Error('Creazione azienda non riuscita');
    companyId = created.id;
    companyName = created.business_name;
    companyCreated = true;
  }

  // 2. Persona: referente puro quando esiste anche l'azienda, altrimenti lead a se`.
  if (wantsPerson && !personId) {
    const asReferente = wantsCompany;
    const created = await deps.createClient(
      {
        entity_type: 'individual',
        business_name: input.person.business_name.trim() || input.person.email.trim(),
        email: input.person.email.trim() || undefined,
        phone: input.person.phone?.trim() || undefined,
        contact_role: input.person.contact_role?.trim() || undefined,
        lead_source: input.leadSource,
        pipeline_id: asReferente ? null : (input.pipelineId || null),
      },
      { asReferente },
    );
    if (!created) throw new Error('Creazione persona non riuscita');
    personId = created.id;
    personName = created.business_name;
    personCreated = true;
  }

  // 3. Collegamento persona - azienda, saltato se gia` presente.
  if (companyId && personId) {
    const already = personCreated || companyCreated
      ? false
      : await (deps.linkExists?.(personId, companyId) ?? Promise.resolve(false));
    if (!already) {
      await deps.linkPersonToCompany({
        person_client_id: personId,
        company_client_id: companyId,
        role: input.person.contact_role?.trim() || null,
        is_referente: true,
        is_primary_contact: true,
        is_primary_company: true,
      });
    }
  }

  // 4. Referenti aggiuntivi presi dai partecipanti del thread.
  if (companyId && input.extraPeople.length > 0) {
    for (const p of input.extraPeople) {
      const created = await deps.createClient(
        {
          entity_type: 'individual',
          business_name: p.name || p.email,
          email: p.email,
          lead_source: input.leadSource,
          pipeline_id: null,
        },
        { asReferente: true },
      );
      if (!created) continue;
      await deps.linkPersonToCompany({
        person_client_id: created.id,
        company_client_id: companyId,
        is_referente: true,
        is_primary_contact: false,
        is_primary_company: true,
      });
      extraCreated += 1;
    }
  }

  return {
    companyId,
    personId,
    companyCreated,
    personCreated,
    extraCreated,
    label: companyName || personName || '',
  };
}
